import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Typography, Button, Space, Spin, Statistic, Tag, Empty } from 'antd';
import {
  AppstoreOutlined, ShoppingOutlined, PhoneOutlined, CalendarOutlined,
  GlobalOutlined, EyeOutlined, TeamOutlined,
  SendOutlined, RobotOutlined, LinkOutlined, ThunderboltOutlined, RocketOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { scenarioAPI, scenarioSiteAdminAPI, couponRequestAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

export default function CustomerHomePage({ currentUser }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scenarios, setScenarios] = useState([]);
  const [siteData, setSiteData] = useState({});
  const [loading, setLoading] = useState(true);
  const [aeoStats, setAeoStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await scenarioAPI.list();
        const list = res.data || [];
        setScenarios(list);

        const siteMap = {};
        for (const sc of list) {
          try {
            const cfgRes = await scenarioSiteAdminAPI.getSiteConfig(sc.id);
            const prodRes = await scenarioSiteAdminAPI.listProducts(sc.id);
            const inqRes = await scenarioSiteAdminAPI.listInquiries(sc.id);
            const evtRes = await scenarioSiteAdminAPI.listEvents(sc.id);
            siteMap[sc.id] = {
              config: cfgRes.data,
              productCount: (prodRes.data || []).length,
              inquiryCount: (inqRes.data || []).length,
              eventCount: (evtRes.data || []).length,
              regCount: (evtRes.data || []).reduce((s, e) => s + (e.registration_count || 0), 0),
            };
          } catch { /* no site config */ }
        }
        setSiteData(siteMap);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await couponRequestAPI.myStats();
        setAeoStats(res.data || null);
      } catch (e) {
        console.error('load aeo stats failed', e);
      }
    })();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const displayName = currentUser?.display_name || currentUser?.username || '';

  return (
    <div>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #722ed1 0%, #1890ff 100%)',
        borderRadius: 16, padding: '36px 40px', marginBottom: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 180, height: 180,
          borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
        }} />
        <Row align="middle">
          <Col flex="auto">
            <Title level={2} style={{ color: '#fff', margin: 0 }}>
              {t('customerHome.welcome', { name: displayName })}
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.75)', margin: '8px 0 0', fontSize: 15 }}>
              {t('customerHome.subtitle')}
            </Paragraph>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<AppstoreOutlined />}
              onClick={() => navigate('/scenarios')}
              style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', fontWeight: 600 }}
            >
              {t('customerHome.myScenarios')}
            </Button>
          </Col>
        </Row>
      </div>

      {/* AI SEO 額度 */}
      <Card
        style={{ marginBottom: 20, borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        styles={{ body: { padding: '18px 24px' } }}
      >
        <Row align="middle" gutter={16} wrap>
          <Col flex="auto">
            <Space direction="vertical" size={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ThunderboltOutlined style={{ color: '#722ed1', fontSize: 20 }} />
                <span style={{ fontWeight: 600, fontSize: 15 }}>{t('customerHome.aeoScansTitle')}</span>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>{t('customerHome.aeoScansSubtitle')}</Text>
            </Space>
          </Col>
          <Col>
            <Space size={28} align="center" wrap>
              <Statistic
                title={t('customerHome.aeoScansRemaining')}
                value={aeoStats?.remaining_scans ?? 0}
                valueStyle={{ color: (aeoStats?.remaining_scans ?? 0) > 0 ? '#52c41a' : '#ff4d4f' }}
                suffix={t('customerHome.aeoScansSuffix')}
              />
              <Statistic title={t('customerHome.aeoScansApproved')} value={aeoStats?.approved_count ?? 0} />
              <Statistic title={t('customerHome.aeoScansPending')} value={aeoStats?.pending_count ?? 0} />
              <Button type="primary" icon={<RocketOutlined />} onClick={() => navigate('/coupon-request')}>
                {t('customerHome.aeoScansButton')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Scenario cards */}
      {scenarios.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Empty description={t('customerHome.noScenarios')} />
          <Button type="primary" icon={<AppstoreOutlined />} onClick={() => navigate('/scenarios')} style={{ marginTop: 16 }}>
            {t('customerHome.createFirst')}
          </Button>
        </Card>
      ) : (
        scenarios.map(sc => {
          const site = siteData[sc.id];
          const hasConfig = !!site?.config;
          const brandColor = site?.config?.brand_color || '#1890ff';

          return (
            <Card
              key={sc.id}
              style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden' }}
              styles={{ body: { padding: 0 } }}
            >
              {/* Scenario header */}
              <div style={{
                background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)`,
                padding: '20px 24px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <Title level={4} style={{ color: '#fff', margin: 0 }}>
                    {site?.config?.brand_name || sc.name}
                  </Title>
                  {sc.description && (
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{sc.description}</Text>
                  )}
                </div>
                <Space>
                  <Tag color={sc.status === 'active' ? 'green' : sc.status === 'draft' ? 'default' : 'orange'}>
                    {sc.status === 'active' ? t('customerHome.statusActive') : sc.status === 'draft' ? t('customerHome.statusDraft') : t('customerHome.statusArchived')}
                  </Tag>
                  {hasConfig && site.config.is_published && (
                    <Tag color="green">{t('customerHome.sitePublished')}</Tag>
                  )}
                </Space>
              </div>

              <div style={{ padding: '20px 24px' }}>
                {/* Stats row */}
                {hasConfig ? (
                  <>
                    <Row gutter={16} style={{ marginBottom: 20 }}>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title={t('customerHome.productsCount')}
                          value={site.productCount}
                          prefix={<ShoppingOutlined style={{ color: brandColor }} />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title={t('customerHome.inquiriesCount')}
                          value={site.inquiryCount}
                          prefix={<PhoneOutlined style={{ color: '#fa541c' }} />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title={t('customerHome.eventsCount')}
                          value={site.eventCount}
                          prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title={t('customerHome.registrationsCount')}
                          value={site.regCount}
                          prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                        />
                      </Col>
                    </Row>

                    {/* Action buttons */}
                    <Space wrap>
                      <Button
                        type="primary"
                        icon={<GlobalOutlined />}
                        onClick={() => navigate(`/scenarios/${sc.id}/site`)}
                        style={{ background: brandColor, borderColor: brandColor }}
                      >
                        {t('customerHome.manageSite')}
                      </Button>
                      {site.config.is_published && (
                        <Button
                          icon={<EyeOutlined />}
                          href={`/s/${site.config.slug}`}
                          target="_blank"
                        >
                          {t('customerHome.previewSite')}
                        </Button>
                      )}
                      <Button
                        icon={<AppstoreOutlined />}
                        onClick={() => navigate(`/scenarios/${sc.id}`)}
                      >
                        {t('customerHome.scenarioEditor')}
                      </Button>
                    </Space>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Paragraph type="secondary">{t('customerHome.noSiteConfig')}</Paragraph>
                    <Button
                      type="primary"
                      icon={<GlobalOutlined />}
                      onClick={() => navigate(`/scenarios/${sc.id}/site`)}
                    >
                      {t('customerHome.createSite')}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })
      )}

      {/* Quick tools */}
      <Title level={4} style={{ marginTop: 32, marginBottom: 16 }}>{t('customerHome.quickTools')}</Title>
      <Row gutter={[16, 16]}>
        {[
          { title: t('customerHome.contactsTool'), icon: <TeamOutlined />, path: '/contacts', color: '#1890ff', desc: t('customerHome.contactsToolDesc') },
          { title: t('customerHome.emailTool'), icon: <SendOutlined />, path: '/email-compose', color: '#722ed1', desc: t('customerHome.emailToolDesc') },
          { title: t('customerHome.chatbotTool'), icon: <RobotOutlined />, path: '/chatbot', color: '#13c2c2', desc: t('customerHome.chatbotToolDesc') },
          { title: t('customerHome.landingPagesTool'), icon: <LinkOutlined />, path: '/landing-pages', color: '#fa541c', desc: t('customerHome.landingPagesToolDesc') },
        ].map(tool => (
          <Col xs={12} sm={6} key={tool.title}>
            <Card
              hoverable
              onClick={() => navigate(tool.path)}
              style={{ borderRadius: 10, height: '100%' }}
              styles={{ body: { padding: 16 } }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${tool.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tool.color, fontSize: 20, marginBottom: 10,
              }}>
                {tool.icon}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{tool.title}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>{tool.desc}</Text>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
